import get from 'lodash.get';
import { defineMessages } from 'react-intl';

import { emptyArray } from 'ui-components/default-props/empty';
import { NumberFormattingService } from '../services/number-formatting-service/';
import { InsuranceProviders } from '../enums/insurance-providers';

const MINIMUM_STAMPS_PURCHASE_AMT = 10;

// TODO: make this use the format currency
const messages = defineMessages({
  insufficientBalance: {
    description: 'costReviewLineItemPurchase.insufficientBalance',
    defaultMessage: 'Insufficient balance. Add funds to continue.',
    id: '7ixcn0',
  },
  insufficientPurchasingPermission: {
    description: 'cost review modal error for no shipping permission for user',
    defaultMessage: 'Your account administrator must enable "Purchasing" permissions for your user.',
    id: 'hpEEid',
  },
});

const canContinuePurchaseForProvider = (providerMaintainsBalance, provider, hasPersonalShipsurance, errorMessage) => {
  const { insuranceProviderId } = provider;
  const isValidInsuranceProviderId = insuranceProviderId !== undefined && insuranceProviderId !== null;
  // HACK SPD-9694: Shipsurance insuranceProvider id backdoor
  if (
    isValidInsuranceProviderId &&
    (insuranceProviderId === InsuranceProviders.Shipsurance.insuranceProviderId || insuranceProviderId === '0') &&
    hasPersonalShipsurance
  ) {
    return true;
  }
  if (isValidInsuranceProviderId || providerMaintainsBalance) {
    return !errorMessage;
  }
  return true;
};

class CostReviewHelperServiceClass {
  costReviewLineItemProviderMeta(provider, userHasPurchasingRole, hasPersonalShipsurance = false) {
    const { balance, minimumPurchaseAmount, subTotal, providerMaintainsBalance, providerErrorMessage, labelProviderId } = provider;
    const providerPurchaseAmount = get(provider, 'purchaseAmount', 0);
    const decimalBalance = NumberFormattingService.roundToPlace(balance, 2);
    const decimalSubTotal = NumberFormattingService.roundToPlace(subTotal, 2);
    const decimalProviderPurchaseAmount = NumberFormattingService.roundToPlace(providerPurchaseAmount, 2);
    // TODO: use the expected currency code when it's available
    const hasUserProvidedInput = decimalProviderPurchaseAmount > 0;
    const minimumPurchaseForThisShipment = decimalSubTotal - decimalBalance;
    const sufficientBalance = decimalBalance >= decimalSubTotal;
    const minPurchaseAmount = NumberFormattingService.roundToPlace(
      Math.max(minimumPurchaseAmount, minimumPurchaseForThisShipment, MINIMUM_STAMPS_PURCHASE_AMT),
      2,
    );
    let errorMessage = !sufficientBalance && decimalProviderPurchaseAmount < minPurchaseAmount ? messages.insufficientBalance : undefined;
    if (!sufficientBalance && !userHasPurchasingRole) {
      errorMessage = messages.insufficientPurchasingPermission;
    }

    const canContinue = canContinuePurchaseForProvider(providerMaintainsBalance, provider, hasPersonalShipsurance, errorMessage);
    return {
      providerMaintainsBalance,
      errorMessage,
      hasUserProvidedInput,
      minPurchaseAmount,
      canContinue,
      sufficientBalance,
      providerErrorMessage,
      labelProviderId,
    };
  }

  calculateSufficientFunding(allProviders, userHasPurchasingRole) {
    return allProviders.every((provider) => {
      const metaProvider = this.costReviewLineItemProviderMeta(provider, userHasPurchasingRole);
      const insuranceProviderId = get(provider, 'insuranceProviderId');
      // HACK SPD-9694: Shipsurance insuranceProvider id backdoor
      if (insuranceProviderId === InsuranceProviders.Shipsurance.insuranceProviderId || insuranceProviderId === '0') {
        return metaProvider.sufficientBalance;
      }
      return !metaProvider.providerMaintainsBalance || metaProvider.sufficientBalance;
    });
  }

  calculateAdjustedProviders(providers, sellerProviders, sellerServices) {
    return providers.map((provider) => {
      const labelProvider = sellerProviders.find((prov) => prov.sellerLabelProviderId === provider.sellerLabelProviderId);
      const lineItemProvider = { ...labelProvider, ...provider };
      const services = get(provider, 'services', emptyArray).map((service) => {
        // TODO: add sellerLabelProviderId to this check when the back-end returns it (isn't at this time)
        const sellerDataService = sellerServices.find((serv) => serv.serviceId === service.shippingServiceId);
        return {
          ...service,
          serviceName: get(sellerDataService, 'name'),
        };
      });
      return {
        ...lineItemProvider,
        services,
      };
    });
  }

  getCombinedWalletCosts(walletProviderGroup) {
    return {
      walletId: walletProviderGroup[0].walletId,
      balance: walletProviderGroup[0].balance,
      // FLin - Assumption that whichever sellerLabelProviderId is used will be resolved to the wallet balance
      sellerLabelProviderId: walletProviderGroup[0]?.sellerLabelProviderId,
      // FLin - As far as I am aware, for SSCS wallet, this should always be true
      providerMaintainsBalance: true,
      // FLin - Integrations team member was not 100% sure if minPurchaseAmts should be summed or not. Grabbing the first for now since it's 1 balance
      minimumPurchaseAmount: walletProviderGroup.reduce((minAmt, prov) => minAmt || prov?.minimumPurchaseAmount, 0),
      subTotal: walletProviderGroup.reduce((grandSubtotal, prov) => (prov.subTotal ?? 0) + grandSubtotal, 0),
      providerInsuranceSubtotal: walletProviderGroup.reduce(
        (grandSubtotal, prov) => (prov?.providerInsuranceSubtotal ?? 0) + grandSubtotal,
        0,
      ),
      providerConfirmationSubtotal: walletProviderGroup.reduce(
        (grandSubtotal, prov) => (prov?.providerConfirmationSubtotal ?? 0) + grandSubtotal,
        0,
      ),
      providerOtherCostSubtotal: walletProviderGroup.reduce(
        (grandSubtotal, prov) => (prov?.providerOtherCostSubtotal ?? 0) + grandSubtotal,
        0,
      ),
      services: walletProviderGroup.reduce((acc, prov) => [...acc, ...(prov?.services ?? [])], []),
      lineItems: walletProviderGroup.reduce((acc, prov) => [...acc, ...(prov?.lineItems ?? [])], []),
    };
  }

  mergeWalletProviders(costReviewData) {
    const { providerCosts, insuranceCosts } = costReviewData;

    const walletProvidersMap = [...providerCosts, ...insuranceCosts]
      .filter((prov) => prov.walletId)
      .reduce((acc, prov) => {
        const walletId = `${prov.walletId}`;
        if (!acc[walletId]) {
          return {
            ...acc,
            [walletId]: [prov],
          };
        }
        acc[walletId].push(prov);
        return acc;
      }, {});

    const walletedProviders = Object.values(walletProvidersMap).map(this.getCombinedWalletCosts);

    const nonWalletedShippingProviders = providerCosts.filter((p) => !p.walletId);

    return {
      ...costReviewData,
      providerCosts: [...nonWalletedShippingProviders, ...walletedProviders],
      walletCosts: Object.values(walletProvidersMap),
    };
  }

  getInsuranceCurrencyCode(insuranceProvider) {
    const { insuranceProviderId } = insuranceProvider;
    // HACK SPD-9694: Shipsurance insuranceProvider id backdoor
    if (insuranceProviderId === InsuranceProviders.Shipsurance.insuranceProviderId || insuranceProviderId === '0') {
      return 'USD';
    }
  }
}

export const CostReviewHelperService = new CostReviewHelperServiceClass();
